import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../../context/NotificationsContext';
import NotificationDropdown from './NotificationDropdown';

/**
 * NotificationBell Component
 * Bell icon with badge showing unread count
 * Clicking opens the dropdown
 *
 * @param {Object} props
 * @param {Function} props.onNavigate - Navigation handler
 * @param {string} props.variant - Style variant: 'light' (default) or 'dark' (for dark backgrounds)
 */
const NotificationBell = ({ onNavigate, variant = 'light' }) => {
    const { unreadCount, refresh } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const bellRef = useRef(null);
    const dropdownRef = useRef(null);

    // Style variants
    const isDark = variant === 'dark';
    const buttonStyles = isDark
        ? 'p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors'
        : 'relative p-2 rounded-lg hover:bg-gray-100 transition-colors';
    const iconStyles = isDark
        ? `w-5 h-5 ${unreadCount > 0 ? 'text-white' : 'text-white/70'}`
        : `w-5 h-5 ${unreadCount > 0 ? 'text-cabaza-purple' : 'text-gray-500'}`;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isOpen &&
                bellRef.current &&
                !bellRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Fetch notifications when opening dropdown
    const handleOpen = () => {
        if (!isOpen) {
            refresh();
        }
        setIsOpen(!isOpen);
    };

    // Handle notification click - navigate to module
    const handleNotificationClick = (notification) => {
        setIsOpen(false);

        // Navigate based on module code
        if (notification.moduleCode === 'BOOKS') {
            onNavigate?.('books');
        } else if (notification.actionUrl) {
            // Parse actionUrl for navigation
            const path = notification.actionUrl.replace('/', '');
            onNavigate?.(path);
        }
    };

    return (
        <div className="relative">
            {/* Bell Button */}
            <button
                ref={bellRef}
                onClick={handleOpen}
                className={buttonStyles}
                aria-label="Notifications"
            >
                <Bell className={iconStyles} />

                {/* Badge */}
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div ref={dropdownRef}>
                    <NotificationDropdown
                        onClose={() => setIsOpen(false)}
                        onNotificationClick={handleNotificationClick}
                        onNavigate={onNavigate}
                    />
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
